import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface NtpfRegionRate {
  region: string;
  weeklyRate: string;
}

export interface FairDealRates {
  ntpfNationalAverage: string;
  ntpfByRegion: NtpfRegionRate[];
  stateFundingPercentage: string;
  averageApplicationWeeks: string;
  totalNursingHomesInScheme: string;
  keyUpdate: string;
  assetContributionRate: string;
  incomeContributionRate: string;
  propertyCapYears: string;
  lastUpdated: string;
}

const CACHE_KEY = "grandpal_fair_deal_rates_v2"; // bumped to invalidate stale cache
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

function isValidRates(r: unknown): r is FairDealRates {
  if (!r || typeof r !== "object") return false;
  const obj = r as Record<string, unknown>;
  return (
    typeof obj.ntpfNationalAverage === "string" &&
    obj.ntpfNationalAverage.startsWith("€") &&
    Array.isArray(obj.ntpfByRegion) &&
    (obj.ntpfByRegion as unknown[]).length > 0
  );
}

export function useFairDealRates() {
  const [rates, setRates] = useState<FairDealRates | null>(null);
  const [citations, setCitations] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check cache first — only serve if data passes validation
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        if (Date.now() - timestamp < CACHE_TTL_MS && isValidRates(data.rates)) {
          setRates(data.rates);
          setCitations(data.citations || []);
          setIsLoading(false);
          return;
        }
        // Invalid or expired — clear it
        localStorage.removeItem(CACHE_KEY);
      }
    } catch (_) {
      localStorage.removeItem(CACHE_KEY);
    }

    const fetchRates = async () => {
      try {
        const { data, error: fnError } = await supabase.functions.invoke("fair-deal-rates");
        if (fnError) throw new Error(fnError.message);

        if (isValidRates(data.rates)) {
          setRates(data.rates);
          setCitations(data.citations || []);
          // Only cache valid data
          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify({
              data: { rates: data.rates, citations: data.citations },
              timestamp: Date.now(),
            }));
          } catch (_) {}
        } else {
          throw new Error("Invalid rates data received");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load rates");
      } finally {
        setIsLoading(false);
      }
    };

    fetchRates();
  }, []);

  return { rates, citations, isLoading, error };
}
